import type { Skill } from "@studio/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Plus } from "lucide-react";
import { useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn, formatClock } from "@/lib/format";
import { Button, Segmented, Sheet, Spinner } from "./ui";

const field =
  "w-full rounded-[var(--radius-control)] border border-line bg-desk px-3 text-[16px] outline-none placeholder:text-mute/70 focus-visible:border-mute";

const EXAMPLE = `# Video prompts for MiniMax-H3

Write the prompt in English, in this order:

1. Who or what is in the shot, with concrete details.
2. What happens, step by step over the length of the clip.
3. The camera: framing, lens and how it moves.
4. What we hear: ambience, effects, speech or music.

Keep one scene per clip. Do not ask for text or logos unless the person wants them.`;

/** The assistant's skills: instructions it loads when a request matches, like skills in Claude Code. */
export function Skills() {
  const queryClient = useQueryClient();
  const skills = useQuery({ queryKey: ["skills"], queryFn: api.skills });
  const [editing, setEditing] = useState<Skill | "new" | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const toggle = useMutation({
    mutationFn: (skill: Skill) => api.updateSkill(skill.id, { enabled: !skill.enabled }),
    onSuccess: (skill) => {
      void queryClient.invalidateQueries({ queryKey: ["skills"] });
      toast.success(skill.enabled ? `${skill.name} is on` : `${skill.name} is off`);
    },
    onError: (error) => toast.error(error.message),
  });
  const remove = useMutation({
    mutationFn: (skill: Skill) => api.deleteSkill(skill.id).then(() => skill),
    onSuccess: (skill) => {
      setConfirmDelete(null);
      void queryClient.invalidateQueries({ queryKey: ["skills"] });
      toast.success(`Deleted ${skill.name}`);
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-[17px] font-semibold">Skills</h2>
          <p className="mt-1 max-w-[62ch] text-[14px] text-mute">
            Instructions the assistant loads when a request matches, for example how to write prompts for video. It
            sees each skill's name and when to use it; the instructions are read only when needed.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setEditing("new")}>
          <Plus className="size-4" aria-hidden />
          Add skill
        </Button>
      </div>

      {skills.isLoading ? <Spinner /> : null}
      {skills.data && skills.data.length === 0 ? (
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="flex items-center gap-3 rounded-[var(--radius-control)] border border-dashed border-line px-4 py-4 text-left text-[14px] text-mute hover:border-mute hover:text-paper"
        >
          <BookOpen className="size-5 shrink-0" aria-hidden />
          No skills yet. Add one to teach the assistant how to handle a kind of request.
        </button>
      ) : null}

      {skills.data?.length ? (
        <ul className="flex flex-col divide-y divide-line/60 rounded-[var(--radius-control)] border border-line">
          {skills.data.map((skill) => (
            <li key={skill.id} className="flex flex-wrap items-start gap-x-3 gap-y-2 px-3 py-3">
              <label className="mt-1 inline-flex cursor-pointer items-center" title={skill.enabled ? "Turn off" : "Turn on"}>
                <input
                  type="checkbox"
                  role="switch"
                  aria-checked={skill.enabled}
                  checked={skill.enabled}
                  onChange={() => toggle.mutate(skill)}
                  className="peer sr-only"
                  aria-label={`${skill.name} ${skill.enabled ? "is on" : "is off"}`}
                />
                <span className="relative h-5 w-9 rounded-full bg-line transition-colors peer-checked:bg-film peer-focus-visible:ring-2 peer-focus-visible:ring-film after:absolute after:left-0.5 after:top-0.5 after:size-4 after:rounded-full after:bg-paper after:transition-transform peer-checked:after:translate-x-4 peer-checked:after:bg-film-ink" />
              </label>
              <button type="button" onClick={() => setEditing(skill)} className="min-w-0 flex-1 text-left">
                <p className={cn("text-[15px] font-medium", !skill.enabled && "text-mute")}>{skill.name}</p>
                <p className="line-clamp-2 text-[14px] text-mute">{skill.description}</p>
                <p className="tabular mt-0.5 text-[12px] text-mute/80">
                  changed {formatClock(skill.updatedAt)}
                  {skill.updatedBy ? ` by ${skill.updatedBy}` : ""}
                </p>
              </button>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="quiet" onClick={() => setEditing(skill)}>
                  Edit
                </Button>
                {confirmDelete === skill.id ? (
                  <>
                    <Button size="sm" variant="danger" onClick={() => remove.mutate(skill)} disabled={remove.isPending}>
                      Delete {skill.name}
                    </Button>
                    <Button size="sm" variant="quiet" onClick={() => setConfirmDelete(null)}>
                      Keep
                    </Button>
                  </>
                ) : (
                  <Button size="sm" variant="quiet" onClick={() => setConfirmDelete(skill.id)}>
                    Delete
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {editing ? (
        <SkillEditor
          skill={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ["skills"] });
          }}
        />
      ) : null}
    </section>
  );
}

function SkillEditor({ skill, onClose, onSaved }: { skill: Skill | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(skill?.name ?? "");
  const [description, setDescription] = useState(skill?.description ?? "");
  const [content, setContent] = useState(skill?.content ?? "");
  const [enabled, setEnabled] = useState(skill?.enabled ?? true);
  const [view, setView] = useState<"write" | "preview">("write");

  const save = useMutation({
    mutationFn: () => {
      const body = { name, description, content, enabled };
      return skill ? api.updateSkill(skill.id, body) : api.createSkill(body);
    },
    onSuccess: (saved) => {
      toast.success(skill ? `Saved ${saved.name}` : `Added ${saved.name}`);
      onSaved();
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <Sheet
      open
      wide
      onOpenChange={(open) => !open && onClose()}
      title={skill ? `Edit ${skill.name}` : "Add a skill"}
      description="The assistant reads the name and “When to use” for every skill that is on, and loads the instructions when a request matches."
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="grid gap-4 md:grid-cols-[minmax(0,260px)_minmax(0,1fr)]">
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium text-mute">Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s+/g, "-"))}
              placeholder="minimax-video-prompts"
              autoCapitalize="none"
              spellCheck={false}
              className={cn(field, "h-10")}
            />
            <span className="text-[12px] text-mute">Lowercase letters, digits and dashes.</span>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium text-mute">When to use</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={400}
              placeholder="When the person wants a video: how to write prompts MiniMax-H3 follows well."
              className={cn(field, "resize-none py-2 leading-6")}
            />
          </label>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[13px] font-medium text-mute">Instructions</span>
            <div className="flex items-center gap-2">
              {!skill && !content ? (
                <Button size="sm" variant="quiet" onClick={() => setContent(EXAMPLE)}>
                  Start from an example
                </Button>
              ) : null}
              <Segmented
                size="sm"
                value={view}
                onChange={setView}
                options={[
                  { value: "write", label: "Write" },
                  { value: "preview", label: "Preview" },
                ]}
              />
            </div>
          </div>
          {view === "write" ? (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={16}
              placeholder="Markdown: steps, rules and examples the assistant should follow."
              className={cn(field, "scrollbar-thin min-h-[40dvh] resize-y py-2.5 text-[15px] leading-6")}
            />
          ) : (
            <div className="chat-prose scrollbar-thin min-h-[40dvh] overflow-y-auto rounded-[var(--radius-control)] border border-line bg-desk px-4 py-3 text-[15px] leading-7">
              {content.trim() ? <Markdown remarkPlugins={[remarkGfm]}>{content}</Markdown> : <p className="text-mute">Nothing written yet.</p>}
            </div>
          )}
          <span className="tabular text-right text-[12px] text-mute">{content.length.toLocaleString()} / 40,000</span>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line/60 pt-3">
          <label className="flex items-center gap-2 text-[14px]">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="size-4 accent-[var(--color-film)]" />
            The assistant may use it
          </label>
          <div className="flex items-center gap-2">
            <Button variant="quiet" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={save.isPending || !name.trim() || !description.trim() || !content.trim()}
            >
              {save.isPending ? "Saving…" : skill ? "Save skill" : "Add skill"}
            </Button>
          </div>
        </div>
      </form>
    </Sheet>
  );
}
